/**
 * Zod schema for the `eventParticipants` collection (ADR-136).
 *
 * Per-event participation, keyed by `personId`. Replaces `eventMemberships`
 * (which was keyed by `uid`, blocking pre-auth participation).
 *
 * Document ID convention: `{eventId}_{personId}`. Pending people get rows
 * the moment they're invited; their authUid arrives later on activation.
 *
 * Note `status: 'pending'` for invited-but-not-claimed and
 * `status: 'active'` once the person authenticates. `status: 'revoked'`
 * preserves the row for audit when the organizer removes the
 * participant (matching the audit-trail discipline of `invitations`
 * per ADR-089).
 */

import { z } from 'zod';
import type { TimestampLike } from '../types/common';
import { roleSchema } from './role';

export const participantStatusSchema = z.enum(['active', 'pending', 'revoked']);

export const eventParticipantDocumentSchema = z.object({
  eventId: z.string(),
  personId: z.string(),
  role: roleSchema,
  status: participantStatusSchema,

  /**
   * Stamped after the per-event preferences gate is reviewed (ADR-040).
   * Replaces `eventMemberships.preferencesReviewedAt`.
   */
  preferencesReviewedAt: z.custom<TimestampLike>().nullable(),

  /**
   * Per-event registration code for the contact-by-code activation
   * handshake. Set when an organizer invites a not-yet-claimed person;
   * null for participants who were active users at invite time.
   */
  invitationCode: z.string().nullable(),

  /** PersonId of the organizer who added this participant. */
  addedByUserId: z.string(),

  /** Set when status transitions to 'revoked'. */
  revokedByUserId: z.string().nullable(),
  revokedAt: z.custom<TimestampLike>().nullable(),
});
